import React, { memo, useMemo, type ReactNode, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { Link } from '@atlassian/jira-router';
import PercentageBar from './percentage-bar';
import { calcPercentage } from './utils';

type Props = {
	iconBefore: ReactNode;
	itemCount: number;
	totalCount: number;
	barColor?: string;
	name: string;
	linkUrl: string;
	linkTarget?: '_self' | '_blank';
	onRowLinkClick?: (event: SyntheticEvent) => void;
};

const PercentageBarRow = ({
	iconBefore,
	itemCount,
	totalCount,
	barColor,
	name,
	linkUrl,
	onRowLinkClick,
	linkTarget,
}: Props) => {
	const percentage = useMemo(() => calcPercentage(totalCount, itemCount), [totalCount, itemCount]);

	return (
		<tr>
			<IdentifierCell>
				{iconBefore}
				<IdentifierName>{name}</IdentifierName>
			</IdentifierCell>
			<td>
				<PercentageBar percentage={percentage} color={barColor} />
			</td>
			<CountCell>
				<Link href={linkUrl} target={linkTarget} onClick={onRowLinkClick}>
					{itemCount}
				</Link>
			</CountCell>
		</tr>
	);
};
export const IDENTIFIER_COLUMN_WIDTH = 160;
export const COUNT_COLUMN_WIDTH = 60;

export default memo<Props>(PercentageBarRow);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IdentifierCell = styled.td({
	display: 'flex',
	alignItems: 'center',
	width: `${IDENTIFIER_COLUMN_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${4.5 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IdentifierName = styled.div({
	marginLeft: token('space.100', '8px'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CountCell = styled.td({
	width: `${COUNT_COLUMN_WIDTH}px`,
	textAlign: 'right',
	fontWeight: 500,
});
